'use client';

import {
  ASSET_PRICE_LOW_VALUE_THRESHOLD,
  PrecisionFloat,
  intlAssetValueFormatting,
  intlFiatValueFormatting,
} from '@bts-web/utils-formatting';
import { useClientLocale, useClientTranslation } from '@bts-web/utils-lokalise';
import { css } from '@bts-web/utils-style-engine';
import { FC, ReactNode, useMemo } from 'react';
import {
  Mutable,
  TradeControllerVariants,
  TradeOperation,
} from '../../../trade/utils';
import { AssetByIdViewQuery } from '@bts-web/data-layer/server';
import { AssetImage, SkeletonElement } from '../../../common';
import { getAppConfig } from '@bts-web/core-app-config';

type MutableNonNullableAsset = NonNullable<
  Mutable<NonNullable<AssetByIdViewQuery['response']>['assetById']>
>;

export type TradeAssetInfoDisplayProps = {
  sourceAssetData?: MutableNonNullableAsset;
  targetAssetData?: MutableNonNullableAsset;
  symbol?: string;
  loaderElement?: ReactNode;
  isDataLoading?: boolean;
  operation: TradeControllerVariants;
  swapExchangeRate?: PrecisionFloat | undefined;
  price?: PrecisionFloat;
  hidePrimaryAssetLogo?: boolean;
};

export const TradeAssetInfoDisplay: FC<TradeAssetInfoDisplayProps> = ({
  sourceAssetData,
  targetAssetData,
  price,
  symbol,
  loaderElement,
  isDataLoading,
  swapExchangeRate,
  operation,
  hidePrimaryAssetLogo,
}) => {
  const locale = useClientLocale();

  const { t } = useClientTranslation();

  const { currency } = getAppConfig();

  const isOperationStakeOrUnstake =
    operation === TradeOperation.stake || operation === TradeOperation.unstake;

  const oneUnitPrice: ReactNode = useMemo(() => {
    if (
      operation === TradeOperation.swap &&
      sourceAssetData &&
      targetAssetData &&
      swapExchangeRate
    ) {
      // Swap mode: show source-to-target conversion
      // Important: show "1 SOURCE = X TARGET" format
      return (
        <div
          className={css({
            display: 'flex',
            gap: 'extra_small_4',
          })}
        >
          <span>1</span>
          <span>{sourceAssetData.symbol}</span>
          <span>=</span>
          <span>
            {intlAssetValueFormatting(swapExchangeRate.value, {
              locale,
              fractionDigits: swapExchangeRate.precision,
            })}
          </span>
          <span>{targetAssetData.symbol}</span>
        </div>
      );
    } else if (
      // Order type: show current price label with asset-to-fiat conversion
      price &&
      operation === TradeOperation.order
    ) {
      return (
        <div
          className={css({
            display: 'flex',
            gap: 'extra_small_3',
          })}
        >
          <span>{t('stop_order_current_price')}</span>
          <span>≈</span>
          <span>
            {intlFiatValueFormatting(
              price.value,
              {
                locale,
                notation: 'standard',
                currency,
                fractionDigits: price.precision,
              },
              ASSET_PRICE_LOW_VALUE_THRESHOLD,
            )}
          </span>
        </div>
      );
    } else if (price) {
      // All other cases: show asset-to-fiat conversion
      return (
        <div
          className={css({
            display: 'flex',
            gap: 'extra_small_2',
          })}
        >
          <span>1</span>
          <span>{symbol}</span>
          <span>=</span>
          <span>
            {intlFiatValueFormatting(
              price.value,
              {
                locale,
                notation: 'standard',
                currency,
                fractionDigits: price?.precision,
              },
              ASSET_PRICE_LOW_VALUE_THRESHOLD,
            )}
          </span>
        </div>
      );
    }
  }, [
    locale,
    currency,
    price,
    symbol,
    swapExchangeRate,
    targetAssetData,
    operation,
    sourceAssetData,
  ]);

  const showPrimaryAssetLogo = !hidePrimaryAssetLogo && sourceAssetData;

  return (
    <div
      data-testid="asset-price-info"
      data-element="details-container"
      className={css({
        display: 'flex',
        alignItems: 'center',
        py: 'small',
        gap: 'extra_small',
        width: '100%',
      })}
    >
      {showPrimaryAssetLogo && (
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            fontSize: 'title.medium_medium',
            overflow: 'hidden',
            width: '100%',
            '& img': {
              flexShrink: 0,
            },
            '& > *': {
              lineClamp: 2,
            },
          })}
        >
          <AssetImage
            width={24}
            height={24}
            src={sourceAssetData.logoUrl as string}
            alt={sourceAssetData.name as string}
            aria-label="asset image"
            placeholder="empty"
          />
          <span>{sourceAssetData.name}</span>
        </div>
      )}
      {isDataLoading && (
        <SkeletonElement width="100" height="20" borderRadius="10%" />
      )}
      {!isDataLoading && !isOperationStakeOrUnstake && oneUnitPrice && (
        <div
          data-element="info"
          className={css({
            color: 'neutrals.text_primary',
            fontSize: 'caption.medium_medium',
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            gap: 'extra_small_2',
            '& > *': {
              whiteSpace: 'nowrap',
            },
          })}
        >
          <span
            aria-label="asset price"
            className={css({
              flex: 1,
              display: 'flex',
              ...(!showPrimaryAssetLogo
                ? {
                    justifyContent: 'flex-end',
                  }
                : {}),
            })}
          >
            {oneUnitPrice}
          </span>

          {loaderElement && <div>{loaderElement}</div>}
        </div>
      )}
    </div>
  );
};
